import * as React from 'react';
import { useCookies } from '../Cookies';
import { CookieEmbedData, NormalizedQuestionnairePageData, CreateEmbedOptions } from './types';
import { Option, PropsWithChildrenOnly } from '@ids/types/utility';
import {
    FetchedUniversalState,
    useFeatureToggle,
    useFetchedUniversalState,
    useUniversalFetch,
    useUniversalFetchingHelpers,
} from '@ids/appshell';
import { FormEmbedResponse, QuestionnairePageResponse } from '@ids/types/now-api';
import { useQueryParams } from '@ids/hooks';

const QuestionnairePageContext = React.createContext<NormalizedQuestionnairePageData | undefined>(undefined);

QuestionnairePageContext.displayName = 'QuestionnairePageContext';

const normalizeQuestionnairePageData = (
    questionnaireData: Option<QuestionnairePageResponse>,
    formEmbedData: CookieEmbedData,
    queryParams: URLSearchParams
): Option<NormalizedQuestionnairePageData> => {
    if (!questionnaireData || !questionnaireData?.data?.length) {
        return undefined;
    }

    const questionnairePageData = questionnaireData?.data[0];

    return {
        sparrowFormToken: formEmbedData?.formToken || questionnairePageData.sparrowFormToken,
        roomsCountQuestionId: questionnairePageData?.roomsCountQuestionId,
        splitRooms: questionnairePageData?.splitRooms ?? true,
        privacyPolicy: questionnairePageData.privacyPolicy,
        sku: queryParams.get('sku') ?? questionnairePageData.idsProductSimpleSku,
        formName: queryParams.get('name'),
    };
};

export const SPARROW_FORM_EMBED_COOKIE = 'wwnow_sparrow_form_embed';
export const SPARROW_FORM_EMBED_COOKIE_OPTIONS = {
    maxAge: 7 * 24 * 60 * 60,
    path: '/',
};

const QuestionnairePageProvider = ({ children }: PropsWithChildrenOnly) => {
    const { get } = useUniversalFetchingHelpers();
    const [cookies, setCookies] = useCookies([SPARROW_FORM_EMBED_COOKIE], SPARROW_FORM_EMBED_COOKIE_OPTIONS);
    const cookieEmbedData: CookieEmbedData = React.useMemo(() => cookies?.get(SPARROW_FORM_EMBED_COOKIE), [cookies]);
    const [formEmbedData, setFormEmbedData] = React.useState<CookieEmbedData>();
    const queryParams = useQueryParams();
    const questionnaireData = useFetchedUniversalState('fetchQuestionnairePage');
    const { p100StudioPriceTiers } = useFeatureToggle();

    useUniversalFetch(
        'fetchQuestionnairePage',
        async () => {
            if (!questionnaireData) {
                return get<FetchedUniversalState['fetchQuestionnairePage']>({
                    path: '/web/v1/content/entries/newIdsQuestionnairePage',
                });
            }
            return undefined;
        },
        [questionnaireData]
    );

    const fetchNewFormEmbedData = React.useCallback(
        async (createEmbedOptions?: CreateEmbedOptions) => {
            const embedResponse = await get<FormEmbedResponse>({
                path: '/web/v1/ids/form/embed',
                queryParams: createEmbedOptions,
            });

            const data = {
                formToken: embedResponse.data?.formToken,
                formId: createEmbedOptions?.formId ?? null,
                channelId: createEmbedOptions?.channelId ?? null,
                contactId: createEmbedOptions?.contactId ?? null,
                name: queryParams.get('name') ?? null,
            };

            if ((p100StudioPriceTiers && data.formToken && data.formId) || (!p100StudioPriceTiers && data.formToken)) {
                setFormEmbedData(data);
                setCookies(SPARROW_FORM_EMBED_COOKIE, JSON.stringify(data));
            }
        },
        [get, p100StudioPriceTiers, queryParams, setCookies]
    );

    React.useEffect(() => {
        if (!formEmbedData) {
            if (
                (p100StudioPriceTiers && cookieEmbedData?.formId === queryParams.get('form-id')) ||
                (!p100StudioPriceTiers && cookieEmbedData?.formToken && !cookieEmbedData?.formId)
            ) {
                setFormEmbedData(cookieEmbedData);
            } else {
                fetchNewFormEmbedData({
                    formId: queryParams.get('form-id'),
                    channelId: queryParams.get('channel-id'),
                    contactId: queryParams.get('contact-id'),
                });
            }
        }
    }, [cookieEmbedData, fetchNewFormEmbedData, formEmbedData, p100StudioPriceTiers, queryParams]);

    const value = React.useMemo(
        () => normalizeQuestionnairePageData(questionnaireData, formEmbedData, queryParams),
        [questionnaireData, formEmbedData, queryParams]
    );

    return <QuestionnairePageContext.Provider value={value}>{children}</QuestionnairePageContext.Provider>;
};

const useQuestionnairePageContext = () => {
    return React.useContext(QuestionnairePageContext);
};

function useResetQuestionnaireEmbed() {
    const [, , removeCookie] = useCookies([SPARROW_FORM_EMBED_COOKIE], SPARROW_FORM_EMBED_COOKIE_OPTIONS);
    const resetFormEmbed = React.useCallback(() => {
        removeCookie(SPARROW_FORM_EMBED_COOKIE);
    }, [removeCookie]);
    return resetFormEmbed;
}

export { QuestionnairePageProvider, useQuestionnairePageContext, useResetQuestionnaireEmbed };
